<template>
  <div class="featured-rides-landing" :dir="isHebrew ? 'rtl' : 'ltr'">
    <title>Featured Rides - TripPlanner</title>
    <SingleRideHeader :presentedText="guideInfoLocalized.featuredRidesLandingHeaderText || t('featuredRidesLandingHeaderBinding')" />
    <b-container>
      <div class="landing-content after_page_header">
        <div class="row">
          <div class="col-12">
            <h1 class="page-title">
              {{ currentPage ? currentPage.title : (guideInfoLocalized.featuredRidesLandingTitleFallback || t('featuredRidesLandingHeaderBinding')) }}
            </h1>
            <p class="page-subtitle">
              {{ currentPage ? currentPage.subtitle : (guideInfoLocalized.featuredRidesLandingSubtitleFallback || t('featuredRidesLandingSubtitle')) }}
            </p>

            <!-- Digital Rides Explanation -->
            <div class="digital-rides-explanation">
              <div class="explanation-content">
                <p class="explanation-text" v-html="formattedExplanation"></p>
                <p v-if="featuredRidesLanguageInfo" class="language-info-text">
                  {{ featuredRidesLanguageInfo }}
                </p>
              </div>
            </div>

            <!-- Author / Guide Section -->
            <section v-if="guideInfoLocalized && guideInfoLocalized.authorTitle" class="digital-section guide-section top-guide-section">
              <h2>{{ guideInfoLocalized.authorTitle }}</h2>
              <div class="digital-two-columns">
                <div class="guide-text">
                  <p class="preserve-newlines">
                    {{ guideInfoLocalized.authorDescription1 }}
                  </p>
                  <p>
                    {{ guideInfoLocalized.authorDescription2 }}
                  </p>
                </div>
                <div class="digital-inline-image">
                  <img
                    :src="guideInfoLocalized.authorImage"
                    :alt="guideInfoLocalized.authorImageAlt"
                  />
                </div>
              </div>
            </section>

            <!-- Consulting Contact Button -->
            <div v-if="guideInfoLocalized && guideInfoLocalized.whatsappNumber" class="whatsapp-contact-container">
              <router-link 
                :to="consultingInfoLink"
                class="whatsapp-contact-btn-link"
              >
                <b-button 
                  variant="success" 
                  class="whatsapp-contact-btn"
                  :aria-label="guideInfoLocalized.consultingButtonText || t('featuredRidesLandingConsultingBinding')"
                >
                  {{ guideInfoLocalized.consultingButtonText || t('featuredRidesLandingConsultingBinding') }}
                </b-button>
              </router-link>
            </div>
            
            <div v-if="currentPage" class="digital-guide-link-container">
              <router-link 
                :to="{ 
                  name: 'digital-guide-info', 
                  query: { manager: currentPage.managerUrlName } 
                }" 
                class="digital-guide-link">
                <i class="fa-solid fa-circle-info"></i>
                {{ guideInfoLocalized.featuredRidesDigitalGuideLinkText || t('digitalGuideWhatIsTitle') }}
              </router-link>
            </div>
          </div>
        </div>

        <div class="category-tabs" v-if="categories.length > 1">
          <button
            v-for="category in categories"
            :key="category.id"
            class="category-tab"
            :class="{ active: currentCategory === category.id }"
            @click="selectCategory(category.id)"
          >
            {{ category.label }}
          </button>
        </div>

        <div class="rides-grid">
          <div 
            v-for="ride in featuredRides" 
            :key="ride.rideId" 
            class="ride-card-wrapper"
          >
            <div class="ride-card" @click="goToRideDetail(ride)">
              <div class="ride-image-container">
                <img :src="ride.image" :alt="getRideField(ride, 'title')" class="ride-image" />
                <div class="ride-price-badge">
                  <span class="price-amount">{{ formatPrice(ride.price) }}</span>
                  <span class="price-currency">{{ ride.currency || currencySymbol }}</span>
                </div>
              </div>
              <div class="ride-card-body">
                <h3 class="ride-title">{{ getRideField(ride, 'title') }}</h3>
                <p class="ride-short-text" v-html="formatTextWithNewlines(getRideField(ride, 'shortText'))"></p>
                <div class="ride-footer">
                  <b-button variant="primary" class="more-info-btn">
                    {{ guideInfoLocalized.featuredRidesMoreInfoButtonText || t('forMoreDetails') }}
                    <i class="fa-solid fa-arrow-left ms-2"></i>
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </b-container>
    <PageFooter
      v-if="!isSubdomain"
    />
  </div>
</template>

<script>
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import config from "@/common/config";
import { mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: "FeaturedRidesLanding",
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader
  },
  computed: {
    ...mapGetters("theme", ["isSubdomain"]),
    currencySymbol () {
      return this.$store.state.regionConfig.currencySymbol
    },
    guideInfo() {
      // Get manager from current page or route
      const managerUrlName = this.currentPage ? this.currentPage.managerUrlName : null;

      const guideInfoConfig = config.config.DIGITAL_GUIDE_INFO || {};
      const managerInfo = managerUrlName && guideInfoConfig[managerUrlName]
        ? guideInfoConfig[managerUrlName]
        : null;

      const defaultInfo = guideInfoConfig.default || {};

      return managerInfo ? { ...defaultInfo, ...managerInfo } : defaultInfo;
    },
    guideInfoLocalized() {
      return this.getLocalizedGuideInfo(this.guideInfo);
    },
    digitalRidesExplanation() {
      // Use custom explanation from currentPage if available, otherwise use default from DIGITAL_GUIDE_INFO
      if (this.currentPage && this.currentPage.explanation) {
        return this.currentPage.explanation;
      }
      return this.guideInfoLocalized.featuredRidesExplanation || '';
    },
    formattedExplanation() {
      const text = this.digitalRidesExplanation;
      return text.replace(this.t('digitalItineraries'), this.t('digitalItinerariesStrong'));
    },
    featuredRidesLanguageInfo() {
      const lang = this.getCurrentLanguage || 'he';
      const template =
        this.guideInfo?.featuredRidesLanguageInfoTemplateByLang?.[lang] ||
        this.guideInfoLocalized.featuredRidesLanguageInfoTemplate;
      if (!template) {
        return null;
      }
      const pageLanguage = this.currentPage?.language || null;
      if (!pageLanguage) {
        return null;
      }
      const languageName = pageLanguage === 'en'
        ? (this.guideInfoLocalized.languageNameEn || 'English')
        : (this.guideInfoLocalized.languageNameHe || this.t('hebrew'));
      return template.replace('{language}', languageName);
    },
    consultingInfoLink() {
      const managerUrlName = this.currentPage ? this.currentPage.managerUrlName : null;
      if (managerUrlName) {
        return {
          name: 'private-consulting-info',
          query: { manager: managerUrlName }
        };
      }
      return { name: 'private-consulting-info' };
    }
  },
  data() {
    return {
      featuredRides: [],
      allRides: [],
      ridePages: [],
      currentPage: null,
      categories: [],
      currentCategory: 'all'
    };
  },
  created() {
    this.initializePages();
  },
  watch: {
    '$route.params.managerUrlName': {
      handler() {
        this.applyCurrentPage();
      }
    },
    getCurrentLanguage() {
      // Rebuild categories and list labels on language change
      this.setupCategories();
      this.applyCategoryFilter();
    }
  },
  methods: {
    getRideField(ride, fieldName) {
      if (!ride || !fieldName) {
        return '';
      }
      const lang = this.getCurrentLanguage || 'he';
      const localizedValue = ride.localized && ride.localized[lang] ? ride.localized[lang][fieldName] : undefined;
      return localizedValue !== undefined && localizedValue !== null && localizedValue !== '' ? localizedValue : ride[fieldName];
    },
    getLocalizedGuideInfo(guideInfo) {
      if (!guideInfo || typeof guideInfo !== 'object') {
        return {};
      }
      const lang = this.getCurrentLanguage || 'he';
      const localized = guideInfo.localized && guideInfo.localized[lang] ? guideInfo.localized[lang] : {};
      return { ...guideInfo, ...localized };
    },
    initializePages() {
      this.ridePages = this.normalizePages(config.config.FEATURED_RIDES || []);
      this.applyCurrentPage();
    },
    normalizePages(rawPages) {
      if (!Array.isArray(rawPages)) {
        return [];
      }
      return rawPages.map((page, index) => {
        const isGroup = Array.isArray(page.rides);
        const normalizedRides = isGroup ? page.rides : [page];
        const fallbackUrlName = page.managerUrlName || `manager-${index + 1}`;
        const slug = typeof page.slug === 'string' ? page.slug : (fallbackUrlName === 'tripplanner' ? null : fallbackUrlName);
        return {
          managerUrlName: fallbackUrlName,
          displayName: page.displayName || page.title || this.t('managerWithIndex', { index }),
          title: page.title || this.t('featuredRidesLandingHeaderBinding'),
          subtitle: page.subtitle || this.t('featuredRidesLandingSubtitle'),
          heroImage: page.heroImage || null,
          explanation: page.explanation || null,
          language: page.language || null,
          slug,
          isDefault: typeof page.isDefault === 'boolean' ? page.isDefault : index === 0,
          rides: normalizedRides
        };
      });
    },
    applyCurrentPage() {
      if (!this.ridePages.length) {
        this.featuredRides = [];
        this.allRides = [];
        this.currentPage = null;
        this.categories = [];
        this.currentCategory = 'all';
        return;
      }
      const slug = this.$route.params.managerUrlName || null;
      let selectedPage = null;
      if (slug) {
        selectedPage = this.ridePages.find(page => page.slug === slug || page.managerUrlName === slug) || null;
      }
      if (!selectedPage) {
        selectedPage = this.ridePages.find(page => page.isDefault) || this.ridePages[0];
        if (slug && selectedPage) {
          // Redirect invalid slugs to the default page for consistency
          this.navigateToPage(selectedPage);
          return;
        }
      }
      this.currentPage = selectedPage;
      if (selectedPage && selectedPage.language) {
        this.$store.dispatch('language/setLanguageFromRide', selectedPage.language);
      }
      this.allRides = (selectedPage && Array.isArray(selectedPage.rides)) ? selectedPage.rides : [];
      this.setupCategories();
      this.applyCategoryFilter();
    },
    navigateToPage(page) {
      if (!page) {
        return;
      }
      const targetName = page.slug ? 'featured-rides-landing-manager' : 'featured-rides-landing';
      if (this.$route.name === targetName) {
        if (!page.slug || this.$route.params.managerUrlName === page.slug) {
          return;
        }
      }
      if (!page.slug) {
        this.$router.push({ name: 'featured-rides-landing' });
        return;
      }
      this.$router.push({
        name: 'featured-rides-landing-manager',
        params: { managerUrlName: page.slug }
      });
    },
    setupCategories() {
      const rides = Array.isArray(this.allRides) ? this.allRides : [];
      if (!rides.length) {
        this.categories = [];
        this.currentCategory = 'all';
        this.featuredRides = [];
        return;
      }

      const categoryMap = new Map();
      rides.forEach((ride, index) => {
        const label = this.getRideField(ride, 'category') || this.t('topDestinations');
        const id = this.normalizeCategoryId(label);
        if (!categoryMap.has(id)) {
          categoryMap.set(id, {
            id,
            label,
            order: index
          });
        }
      });

      // If all rides have the same category, don't show category tabs
      if (categoryMap.size <= 1) {
        this.categories = [];
        this.currentCategory = 'all';
        this.featuredRides = this.allRides;
        return;
      }

      const categories = [
        { id: 'all', label: this.t('allDestinations'), order: -1 },
        ...Array.from(categoryMap.values()).sort((a, b) => a.order - b.order)
      ];

      this.categories = categories;
      this.currentCategory = 'all';
    },
    selectCategory(categoryId) {
      if (!categoryId || this.currentCategory === categoryId) {
        return;
      }
      this.currentCategory = categoryId;
      this.applyCategoryFilter();
    },
    applyCategoryFilter() {
      if (!this.currentCategory || this.currentCategory === 'all') {
        this.featuredRides = this.allRides;
        return;
      }
      this.featuredRides = this.allRides.filter(ride => {
        const rideCategoryId = this.normalizeCategoryId(ride.category || '');
        return rideCategoryId === this.currentCategory;
      });
    },
    normalizeCategoryId(label) {
      if (!label || typeof label !== 'string') {
        return 'uncategorized';
      }
      return label
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^א-תa-z0-9\s-]/gi, '')
        .replace(/\s+/g, '-')
        || 'category';
    },
    formatPrice(price) {
      const locale = this.getCurrentLanguage === 'en' ? 'en-US' : 'he-IL';
      return price.toLocaleString(locale);
    },
    formatTextWithNewlines(text) {
      if (!text) return '';
      return text.replace(/\n/g, '<br>');
    },
    goToRideDetail(ride) {
      if (!ride || !ride.rideId) {
        return;
      }
      const rideLanguage = ride.language || this.currentPage?.language || null;
      if (rideLanguage) {
        this.$store.dispatch('language/setLanguageFromRide', rideLanguage);
      }
      this.$router.push({ name: 'featured-ride-detail', params: { id: ride.rideId } });
    }
  },
  metaInfo() {
    const pageTitle = this.currentPage ? `${this.currentPage.title} - TripPlanner` : 'Featured Rides - TripPlanner';
    const description = this.currentPage ? this.currentPage.subtitle : 'Discover our featured trips';
    return {
      title: pageTitle,
      link: [
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' }
      ]
    };
  }
};
</script>

<style scoped>
.language-info-text {
  margin-top: 1.2rem;
  font-size: 1.4rem;
  color: #495057;
}

[dir="ltr"] .explanation-content,
[dir="ltr"] .ride-card-body,
[dir="ltr"] .guide-section {
  text-align: left;
}

[dir="ltr"] .page-title,
[dir="ltr"] .page-subtitle {
  direction: ltr;
}

.landing-content {
  min-height: 60vh;
  padding: 3rem 0;
}

.page-title {
  font-size: 3rem;
  color: #0d2f29;
  text-align: center;
  margin-bottom: 1rem;
  font-weight: bold;
}

.page-subtitle {
  font-size: 2.1rem;
  color: #444;
  text-align: center;
  margin-bottom: 2.5rem;
  line-height: 1.8;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.digital-rides-explanation {
  max-width: 900px;
  margin: 0 auto 2rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(7, 160, 195, 0.08) 0%, rgba(52, 208, 182, 0.08) 100%);
  border-radius: 12px;
  border-right: 4px solid #07A0C3;
}

.explanation-content {
  text-align: right;
}

.explanation-text {
  font-size: 1.6rem;
  color: #333;
  line-height: 1.8;
  margin: 0;
}

.explanation-text strong {
  color: #07A0C3;
  font-weight: 600;
}

.whatsapp-contact-container {
  text-align: center;
  margin: 2.5rem 0;
}

.whatsapp-contact-btn-link {
  text-decoration: none;
  display: inline-block;
}

.whatsapp-contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.2rem 2.4rem;
  font-size: 1.8rem;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(7, 160, 195, 0.3);
}

.whatsapp-contact-btn:hover {
  background: linear-gradient(45deg, #07A0C3, #34D0B6);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(7, 160, 195, 0.4);
  color: white;
}

.whatsapp-contact-btn:active {
  transform: translateY(0);
}

.whatsapp-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.digital-guide-link-container {
  text-align: center;
  margin-bottom: 2rem;
}

.digital-guide-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #07A0C3;
  font-size: 1.6rem;
  text-decoration: none;
  padding: 0.8rem 1.6rem;
  border: 2px solid #07A0C3;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.digital-guide-link:hover {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: #fff;
  border-color: transparent;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.3);
}

.category-tabs {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
}

.category-tab {
  border: 1px solid #07A0C3;
  background: transparent;
  color: #07A0C3;
  padding: 0.6rem 1.6rem;
  border-radius: 999px;
  font-size: 1.4rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-tab.active,
.category-tab:hover {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: #fff;
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.2);
}

.rides-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.ride-card-wrapper {
  display: flex;
}

.ride-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ride-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
}

.ride-image-container {
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
}

.ride-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.ride-card:hover .ride-image {
  transform: scale(1.05);
}

.ride-price-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.price-amount {
  font-size: 1.8rem;
  font-weight: bold;
}

.price-currency {
  font-size: 1.4rem;
}

.ride-card-body {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  text-align: right;
}

.ride-title {
  font-size: 2rem;
  color: #0d2f29;
  margin-bottom: 1rem;
  font-weight: bold;
}

.ride-short-text {
  font-size: 1.6rem;
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  flex-grow: 1;
}

.ride-footer {
  margin-top: auto;
}

.more-info-btn {
  width: 100%;
  padding: 1rem;
  font-size: 1.6rem;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border: none;
  border-radius: 8px;
  font-weight: 600;
}

.more-info-btn:hover {
  background: linear-gradient(45deg, #07A0C3, #34D0B6);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.3);
}

/* Guide Section Styles */
.guide-section {
  margin-top: 5rem;
  background: #ffffff;
  padding: 4rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  text-align: right;
}

.top-guide-section {
  margin-top: 0;
  margin-bottom: 2.5rem;
  padding: 2rem 3rem;
  background: linear-gradient(135deg, rgba(52, 208, 182, 0.08) 0%, rgba(7, 160, 195, 0.08) 100%);
  border: 1px solid rgba(7, 160, 195, 0.1);
}

.top-guide-section h2 {
  font-size: 2.2rem;
  margin-bottom: 1.5rem;
  text-align: right;
}

[dir="ltr"] .top-guide-section h2 {
  text-align: left;
}

.top-guide-section h2::after {
  right: 0;
  transform: none;
  width: 50px;
  height: 3px;
}

.top-guide-section .guide-text p {
  font-size: 1.5rem;
  line-height: 1.6;
  margin-bottom: 0.8rem;
}

.top-guide-section .digital-inline-image {
  flex: 0 0 150px;
}

.top-guide-section .digital-inline-image img {
  width: 130px;
  height: 130px;
  object-fit: cover;
  border-radius: 50%; /* Circular avatar style */
  border: 3px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.digital-two-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
  align-items: center;
}

.guide-text {
  flex: 1 1 400px;
}

.guide-text p {
  font-size: 2rem;
  line-height: 1.9;
  margin-bottom: 1.6rem;
  color: #495057;
}

.guide-text p.preserve-newlines {
  white-space: pre-line;
}

.digital-inline-image {
  flex: 1 1 300px;
  text-align: center;
}

.digital-inline-image img {
  max-width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

@media (max-width: 768px) {
  .landing-content {
    padding: 2rem 1rem 3rem;
  }

  .page-title {
    font-size: 2.2rem;
  }

  .page-subtitle {
    font-size: 1.6rem;
  }

  .rides-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .ride-image-container {
    height: 200px;
  }

  .ride-card-body {
    padding: 1.5rem;
  }

  .ride-title {
    font-size: 1.6rem;
  }

  .ride-short-text {
    font-size: 1.4rem;
  }

  .digital-rides-explanation {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .explanation-text {
    font-size: 1.4rem;
  }

  .top-guide-section {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .top-guide-section .digital-inline-image {
    flex: 0 0 100px;
    order: -1; /* Image first on mobile */
  }

  .top-guide-section .digital-inline-image img {
    width: 80px;
    height: 80px;
  }

  .top-guide-section h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .top-guide-section h2::after {
    right: 50%;
    transform: translateX(50%);
  }

  .top-guide-section .guide-text p {
    font-size: 1.4rem;
  }

  .top-guide-section .guide-text {
    flex: 1 1 auto;
  }

  .guide-section {
    padding: 2rem;
    margin-top: 3rem;
  }

  .guide-section h2 {
    font-size: 2.4rem;
    margin-bottom: 2rem;
  }

  .digital-two-columns {
    flex-direction: column;
    gap: 2rem;
  }

  .guide-text p {
    font-size: 1.8rem;
  }

  .guide-text {
    flex: 1 1 auto;
  }

  .digital-inline-image {
    flex: 1 1 auto;
  }

  .whatsapp-contact-container {
    margin: 2rem 0;
  }

  .whatsapp-contact-btn {
    font-size: 1.6rem;
    padding: 1rem 2rem;
  }

  .whatsapp-icon {
    width: 20px;
    height: 20px;
  }
}
</style>


